import React from "react";
import { Button } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { ChakraFAIcon } from "@/components/ui/Icon";
import { getFeatures } from "stores/classwizeConfigStore";
import { useFeatureFlagsQuery } from "@/query/featureFlagsQuery";
import { useUserSessionStore } from "@/stores/v2/userSessionStore";
import { useSelectedClassroomStore } from "@/stores/v2/selectedClassroomStore";
import { FeatureFlag } from "@/api/classroomFeatureFlags";
import { captureSeatPlanToolbarActionClickedEvent, SeatPlanToolbarAction } from "@/utils/posthogUtils";
import {
	FloatingToolbar,
	FloatingToolbarDivider,
	FloatingToolbarTitle,
	floatingToolbarGhostButtonProps,
	floatingToolbarMenuItemProps,
} from "./FloatingToolbar";
import { ToolbarMenu } from "./ToolbarMenu";

interface SelectionToolbarProps {
	count: number;
	/** Selected students that are not currently paused (can be paused). */
	pausableCount: number;
	/** Selected students that are currently paused (can be resumed). */
	resumableCount: number;
	/** Selected students that are not currently screensharing (can be added). */
	screenshareAddCount: number;
	anyScreensharing: boolean;
	/** Actions that require a live class session are disabled when false. */
	isClassActive: boolean;
	/** Whether a focus is currently applied to the class. Determines whether "Add" merges into the active focus or opens the drawer. */
	hasActiveFocus: boolean;
	/** Selected students that would actually be added to focus (excludes those already in the active focus). */
	focusAddCount: number;
	/** Selected students that are currently in the active focus (and so can be removed from it). */
	focusRemoveCount: number;
	/** Opens the Focus Library drawer to apply a focus to the selection (used when no focus is active yet; Focus opens the drawer directly). */
	onAddFocus: () => void;
	/** Adds the selected students straight to the already-active focus, without opening the drawer. */
	onAddToActiveFocus: () => void;
	/** Removes the selected students from the already-active focus. */
	onRemoveFromActiveFocus: () => void;
	onPause: () => void;
	onResume: () => void;
	onAnnouncement: () => void;
	onOpenTab: () => void;
	onScreenshare: () => void;
	onEndScreenshare: () => void;
	onClear: () => void;
}

export function SelectionToolbar(props: SelectionToolbarProps): JSX.Element {
	const { t } = useTranslation();

	const classwizeFeatures = getFeatures();
	const activeDevice = useUserSessionStore((state) => state.active_device);
	const teacherUsername = useUserSessionStore((state) => state.active_username);
	const classroomName = useSelectedClassroomStore((s) => s.name);
	const featureFlagsQuery = useFeatureFlagsQuery(activeDevice as string);

	const showFocus = classwizeFeatures.includes("focus");
	const showPause = classwizeFeatures.includes("lock");
	const showAnnouncement = classwizeFeatures.includes("message_class");
	const showOpenTab = classwizeFeatures.includes("open_tab");
	const showScreenshare = !!featureFlagsQuery.data?.includes(FeatureFlag.SCREEN_SHARE);

	const trackToolbarAction = (action: SeatPlanToolbarAction) => {
		if (teacherUsername && activeDevice && classroomName) {
			captureSeatPlanToolbarActionClickedEvent(teacherUsername, activeDevice, classroomName, action, props.count);
		}
	};

	return (
		<FloatingToolbar>
			<FloatingToolbarTitle>{t("seatPlan.nSelected", { count: props.count })}</FloatingToolbarTitle>
			<FloatingToolbarDivider />

			{showFocus &&
				(props.hasActiveFocus ? (
					<ToolbarMenu
						label={t("seatPlan.focus")}
						icon="fa-arrows-to-eye"
						buttonTestId="seatplan-focus-menu-button"
						popupId="seatplan-focus-menu-popup"
						onButtonClick={() => trackToolbarAction("focus")}
					>
						{props.focusAddCount > 0 && (
							<Button {...floatingToolbarMenuItemProps} data-testid="seatplan-focus-add-button" onClick={props.onAddToActiveFocus}>
								{t("seatPlan.addToFocus", { count: props.focusAddCount })}
							</Button>
						)}
						{props.focusRemoveCount > 0 && (
							<Button {...floatingToolbarMenuItemProps} data-testid="seatplan-focus-remove-button" onClick={props.onRemoveFromActiveFocus}>
								{t("seatPlan.removeFromFocus", { count: props.focusRemoveCount })}
							</Button>
						)}
					</ToolbarMenu>
				) : (
					<Button
						{...floatingToolbarGhostButtonProps}
						leftIcon={<ChakraFAIcon icon="fa-arrows-to-eye" variant="solid" boxSize={4} />}
						data-testid="seatplan-focus-button"
						onClick={() => {
							trackToolbarAction("focus");
							props.onAddFocus();
						}}
					>
						{t("seatPlan.focus")}
					</Button>
				))}

			{showPause && (
				<ToolbarMenu
					label={t("seatPlan.pause")}
					icon="fa-pause"
					isDisabled={!props.isClassActive}
					buttonTestId="seatplan-pause-menu-button"
					popupId="seatplan-pause-menu-popup"
					onButtonClick={() => trackToolbarAction("pause")}
				>
					{props.pausableCount > 0 && (
						<Button {...floatingToolbarMenuItemProps} data-testid="seatplan-pause-button" onClick={props.onPause}>
							{t("seatPlan.pauseN", { count: props.pausableCount })}
						</Button>
					)}
					{props.resumableCount > 0 && (
						<Button {...floatingToolbarMenuItemProps} data-testid="seatplan-resume-button" onClick={props.onResume}>
							{t("seatPlan.resumeN", { count: props.resumableCount })}
						</Button>
					)}
				</ToolbarMenu>
			)}

			{showAnnouncement && (
				<Button
					{...floatingToolbarGhostButtonProps}
					isDisabled={!props.isClassActive}
					leftIcon={<ChakraFAIcon icon="fa-megaphone" variant="solid" boxSize={4} />}
					data-testid="seatplan-announcement-button"
					onClick={() => {
						trackToolbarAction("announcement");
						props.onAnnouncement();
					}}
				>
					{t("seatPlan.announcement")}
				</Button>
			)}
			{showOpenTab && (
				<Button
					{...floatingToolbarGhostButtonProps}
					isDisabled={!props.isClassActive}
					leftIcon={<ChakraFAIcon icon="fa-browser" variant="solid" boxSize={4} />}
					data-testid="seatplan-open-tab-button"
					onClick={() => {
						trackToolbarAction("open_tab");
						props.onOpenTab();
					}}
				>
					{t("seatPlan.openTab")}
				</Button>
			)}

			{showScreenshare && (
				<ToolbarMenu
					label={t("seatPlan.screenshare")}
					icon="fa-screencast"
					isDisabled={!props.isClassActive}
					buttonTestId="seatplan-screenshare-menu-button"
					popupId="seatplan-screenshare-menu-popup"
					onButtonClick={() => trackToolbarAction("screenshare")}
				>
					<Button
						{...floatingToolbarMenuItemProps}
						data-testid="seatplan-screenshare-add-button"
						isDisabled={props.screenshareAddCount === 0}
						onClick={props.onScreenshare}
					>
						{t("seatPlan.addNStudents", { count: props.screenshareAddCount })}
					</Button>
					{props.anyScreensharing && (
						<Button {...floatingToolbarMenuItemProps} data-testid="seatplan-screenshare-end-button" onClick={props.onEndScreenshare}>
							{t("seatPlan.endScreenShare")}
						</Button>
					)}
				</ToolbarMenu>
			)}

			<FloatingToolbarDivider />
			<Button {...floatingToolbarGhostButtonProps} data-testid="seatplan-clear-selection-button" onClick={props.onClear}>
				{t("seatPlan.clear")}
			</Button>
		</FloatingToolbar>
	);
}
